/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 7 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation, untranslated } from "@i18n";
import get from "lodash/get";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { RootState } from "@Reducers/index";
import { useState, useEffect, useRef, useCallback, forwardRef, useContext, useMemo } from "react";
import MultiSelectComponent from "@CubeComponents/form/MultiselectComponent";
import { GrClose } from "react-icons/gr";
import ChipListComponent, {
  ChipsDirective,
  ChipDirective,
} from "@CubeComponents/form/ButtonComponent/ChipListComponent";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import CalibrationModal from "./CalibrationModal";
import CreateNewTakeoffModal from "./CreateNewTakeoffModal";
import * as ACTIONS from "@Actions/estimation";
import { setEstimateFieldsNeedtoRefresh, estimateFieldsNeedToRefreshIntialState } from "@Reducers/EstimateList";
import { getViewerUrl } from "@Helpers";
import { estimateMeasurementsUrl } from "@Services/dataManager";
import OpenInViewerIcon from "@Components/StickySidebarViewer/assets/OpenInViewerIcon";
import { setModalStatus, setSelectedNestedMeasurements } from "@Reducers/Viewer";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { GetEstimateDataProp, getEstimateData } from "@Actions/EstimateList";
import {
  DuplicateTakeoffModal,
  EstimateNotFoundPage,
  ManualMeasurements as ManualMeasurement,
  getMeasurementsListColumns,
} from "@Features/estimation";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_NORMAL } from "@Services/constants";
import { getFileversionIdAndNodeOffsetIdMap } from "@Services/cubeViewHelper";
import { folderPermissions } from "@Helpers/constants";
import IconComponent from "@CubeComponents/icons";
import SelectDropDown, { type SelectEventArgs } from "@CubeComponents/Mui_Form/SelectDropDown";
import {
  CubeGlobal,
  HoopsViewerContext,
  getAggregateMeasurementBoundingBox,
  useViewerCallbacks,
} from "@Features/viewer-core";
import { setIsMeasurementStarted } from "@Reducers/measurements";
import { dispatchModelLoadingToast, updateMeasurementAttributePopup } from "@Actions/Viewer";
import { getIsViewerDependencyLoaded } from "@Services/utils/helpers";
import DeriveMeasurements from "./DeriveMeasurements";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { Box, Tab, Tabs } from "@mui/material";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import Text from "@CubeComponents/html/Text/Text";
import { createUuid, OperatorId, Selection } from "@ts3d-hoops/web-viewer";
import type { MeasurementItem } from "@ServerTypes/measurements/measurements.schema";
import { objectIdString, type ObjectIdString } from "@Helpers/schemas/common.schema";
import type { ComponentProps, ContextType, MutableRefObject } from "react";
// `Box` is aliased because @mui/material's own `Box` component is imported above.
import type { Box as HoopsBox, CallbackMap, SheetId } from "@ts3d-hoops/web-viewer";
import type {
  GridClickEvent,
  GridRowBase,
  SlickGrid1Ref,
  ToolbarItem,
  ValidatedColumnDefinition,
} from "@CubeComponents/html/SlickGrid1";
import type { TranslatedString } from "@i18n";

const { RESOURCE_VIEW, RESOURCE_MODIFY } = folderPermissions();

/**
 * The `EstimateDataWithDisplayUnits` alias that used to sit here is gone: all five display units —
 * and their `factor` / `type` / `selectedSymbol` members — are `.optional()` on the estimate
 * response (`displayUnitSerializedSchema` in estimates.schema.ts), so an estimate that has not had
 * every dimension configured really does reach here with holes. The two readers below now handle
 * the holes instead of asserting them away.
 */

/**
 * The `MeasurementRow` alias that used to sit here is gone: `measurementItemSchema` now declares
 * `unit` (with its branded `id` / `type.id`), `multiplier`, `nature` and `takeOffs` itself, so the
 * redux `MeasurementItem` rows carry them without being asserted into a narrower local type. The
 * members are optional there, which is what the reads below now handle.
 */

/**
 * The BIM/node link a 3D measurement carries. The old SCHEMA GAP note here is gone:
 * `measurementItemSchema.elements` now declares the single stored object the Model-to-Takeoff wizard
 * writes, matching `measurements.collection.ts` and the data (0 of 788,592 Measurements on staging
 * hold an array). This alias only pins the two members the 3D paths below read; both stay optional so
 * a server row still flows in unasserted.
 */
type MeasurementNodeElement = NonNullable<MeasurementItem["elements"]> & {
  nodeId?: number;
  fileVersionId?: string;
};

/**
 * A row of the measurements grid: the server `MeasurementItem` plus SlickGrid's own row members and
 * the aggregation-added fields. Everything added here is optional, so the redux `MeasurementItem[]`
 * flows into this type unasserted.
 *
 * `elements` and `tags` accept `null` because the server sends `null`, not just absence — the stored
 * fields are nullable (`measurements.collection.ts`) and staging (cube-qa) has both: of the 253,215
 * Measurements carrying `elements`, 41,626 hold `null`; 11 documents hold a `null` `tags`. Every read
 * of either below is behind a truthiness check, which rejects `null` as well as `undefined`, so the
 * `!`s that follow those checks stay valid.
 */
type MeasurementGridRow = MeasurementItem &
  GridRowBase & {
    elements?: MeasurementNodeElement | null;
    tags?: string[] | null;
    isSystemGenerated?: boolean;
    isParentFileActive?: boolean;
    projectId?: ObjectIdString;
  };

/** fileVersionId -> HOOPS node-id offset, as the untyped `getFileversionIdAndNodeOffsetIdMap` returns it. */
type NodeOffsetMap = Record<string, number>;

/**
 * GRID GAP: `SlickGrid1Ref` does not declare `dataset`; this panel pushes the fetched rows straight
 * onto the instance when the redux list changes.
 */
type MeasurementsGridRef = SlickGrid1Ref<MeasurementGridRow> & { dataset?: MeasurementGridRow[] };

/**
 * The markup-appearance fields a takeoff carries (`takeoffs.schema` types them as number-or-string
 * unions because the appearance form round-trips them through text inputs).
 */
type TakeoffMarkupStyle = {
  lineColor?: string | null;
  fillColor?: string | null;
  lineThickness?: number | string | null;
  fillOpacity?: number | string | null;
  symbolSize?: number | string | null;
  countSymbol?: number | string | null;
};

/** The four quantity-takeoff tool toggles `handleToolsVisibility` switches. */
type MeasurementToolsVisibility = { linear: boolean; area: boolean; volume: boolean; count: boolean };

/**
 * A HOOPS measure markup as this panel handles it. `_name`, `_measurementValue`, `_unitMultiplier`
 * and `cubeUniqueId` are internals reached by name (the last one is CUBE's own tag on the markup),
 * and the four `derive*` members exist only on the markup classes that support them — so the markup
 * is described structurally rather than through `MeasureMarkup`.
 */
type CubeMeasureMarkup = {
  cubeUniqueId: string;
  _name: string;
  _measurementValue: number;
  _unitMultiplier: number;
  calScale?: string;
  id?: number;
  toJson(): unknown;
  select(): void;
  unSelect(): void;
  getBoundingBox(): HoopsBox;
  getNestedMeasurements?: () => { name: string; value: number; type: { value: string } }[];
  derivePerimeterMeasurement?: () => CubeMeasureMarkup | CubeMeasureMarkup[];
  deriveSurfaceAreaMeasurement?: () => CubeMeasureMarkup | CubeMeasureMarkup[];
  deriveCircumferenceMeasurement?: () => CubeMeasureMarkup | CubeMeasureMarkup[];
  deriveLengthSegmentsMeasurement?: () => CubeMeasureMarkup | CubeMeasureMarkup[];
};

/**
 * The three shapes `saveMeasurements` walks its `nestedMeasurements` local through: the markup's raw
 * nested list, the mapped payload rows, then the serialized JSON string it sends.
 */
type NestedMeasurementStage =
  | ReturnType<NonNullable<CubeMeasureMarkup["getNestedMeasurements"]>>
  | { type: string; originalValue: number; alteredValue: number; name: string }[]
  | string
  | null;

/**
 * The SlickGrid formatter signature `getMeasurementsListColumns` accepts, bound to this panel's row
 * type. Parameters are `(row, cell, value, columnDef, dataContext)`; the first four are unused by
 * every template here, hence the placeholder names kept verbatim from the original.
 */
type MeasurementCellTemplate = (
  row: number,
  cell: number,
  value: unknown,
  columnDef: unknown,
  dataContext: MeasurementGridRow,
) => string | HTMLElement;

/** One row of a measurement's nested breakdown, as the chip list renders it. */
type NestedMeasurementChip = { name: string; type: string; alteredValue: number };

/** A toolbar entry as this panel builds it: `ToolbarItem` plus the `name` label the toolbar reads. */
type MeasurementToolbarItem = ToolbarItem & { name?: TranslatedString };

/** One entry of the derive-measurement tab strip. */
type DerivedMeasurementOption = { text: string; value: string; reference: MutableRefObject<unknown> };

/**
 * A derived markup once `generateDriveMeasurementData` has numbered it: still a HOOPS markup, and
 * also a row of the derive grid (which requires the `id` that numbering assigns).
 */
type DerivedMeasurementMarkup = CubeMeasureMarkup & ComponentProps<typeof DeriveMeasurements>["dataSource"][number];

/** Derived markups bucketed by `DerivedMeasurementOption.value` (perimeter, surfaceArea, ...). */
type DerivedMeasurementBuckets = Record<string, DerivedMeasurementMarkup[]>;

/** The row the inline name/multiplier editor holds while a measurement is being edited. */
type EditingRowValue = {
  measurementId?: string;
  name?: string;
  multiplier?: number | string;
  takeOffs?: MeasurementItem["takeOffs"];
};

/**
 * Props as the call sites pass them: Viewer, the estimate's Measurements tab, TakeoffProperties,
 * AddReferenceList, and TakeoffDetails — which spreads its own props object in. Everything but
 * `source` is optional because no single caller passes the whole set.
 */
type MeasurementsProps = {
  source: string;
  openAsPopup?: boolean;
  showDynamic?: boolean;
  estimateId?: string;
  fileVersionId?: string;
  takeOffItemId?: string;
  takeOffIncludedMeasuremnt?: boolean;
  takeoffType?: string;
  viewerImpl?: ContextType<typeof HoopsViewerContext>["viewerImpl"];
  quantityTakeoffToolbarVisibility?: (value: boolean) => void;
  /**
   * CALLER GAP: TakeoffDetails declares this `(value: boolean) => void`, although the only
   * implementation (Viewer's `setShowQuantityToolbarTools`) takes the four-tool object below.
   * Declared method-style over the union so the spread from TakeoffDetails still checks.
   */
  handleToolsVisibility?(value: MeasurementToolsVisibility | boolean): void;
  setBtnState?: (enabled: boolean) => void;
  deleteMeasurementFromTakeOff?: (data: { measurementIds: string[] }) => Promise<unknown>;
  handleRowSelection?: () => void;
  // Forwarded by Viewer / TakeoffDetails but not read here.
  isTakeoffsModalOpen?: boolean;
  setIsTakeoffsModalOpen?: (value: boolean) => void;
  isMeasurementsModalOpen?: boolean;
  setIsMeasurementsModalOpen?: (value: boolean) => void;
  estimateNameId?: string | number;
  estimateName?: string;
};

/**
 * The three ids as the body reads them. They reach this panel as plain strings — a route param
 * (`Estimate.tsx` passes `params.estimateId`) and the viewer's own untyped prop chain — and every
 * request built from them below is re-validated server-side by the same `objectIdString` schema
 * (measurements/takeoffs/estimates .schema.ts). Branded once here rather than at each request.
 *
 * The three optional callbacks are NO LONGER re-declared as required: `Estimate.tsx` really does
 * render this panel without them, and each is now invoked through the call sites' own guards.
 */
type MeasurementsPropsInternal = Omit<MeasurementsProps, "estimateId" | "fileVersionId" | "takeOffItemId"> & {
  estimateId: ObjectIdString;
  fileVersionId: ObjectIdString;
  takeOffItemId: ObjectIdString;
};

/** The live viewer wrapper, once one is mounted. */
type LoadedViewerImpl = NonNullable<ContextType<typeof HoopsViewerContext>["viewerImpl"]>;

const TEMP_MARKUP_NAMES = new Set([
  "ThreePointsAngleMarkup",
  "MeasurePointPointDistance",
  "CustomAreaMarkup",
  "QTakeOffPoint3DMarkup",
]);

const MEASUREMENT_TOOLBAR_IDS = {
  START: "start_measurement",
  EDIT: "edit_measurement",
  DELETE: "delete_measurement",
  ADD_TO_TAKEOFFS: "add_to_takeoffs",
  DUPLICATE: "duplicate_measurement",
  DERIVE: "derive_measurement",
  SITE_MEASUREMENT: "site_measurement",
  SAVE: "save_measurement",
  CANCEL: "cancel_measurement",
};

// The forwarded ref is declared `unknown` because callers keep narrower handles for it (e.g.
// TakeoffDetails' two-method `MeasurementsGridHandle`); the grid API is kept typed on `dataGrid`.
const Measurements = forwardRef<unknown, MeasurementsProps>(({ openAsPopup = false, ...props }, ref) => {
  const { t } = useTranslation();
  const { isModelStructureReady, isSettingsApplied, viewerImpl, showCalibrationModal, handleCalibrationModal } =
    useContext(HoopsViewerContext);

  /**
   * The mounted viewer, for the paths that only run with one — either behind
   * `getIsViewerDependencyLoaded` (which keeps its viewer slots `any`, so tsc cannot narrow through
   * it) or on a viewer-only interaction. `viewerImpl` is genuinely null on the estimate's
   * Measurements tab, which renders this panel outside the Viewer's Provider, so it is left nullable
   * rather than asserted away at the `useContext` call; this throws where dereferencing the null
   * previously threw, instead of hiding it from the type.
   */
  const getViewer = (): LoadedViewerImpl => {
    if (!viewerImpl) throw new Error("Measurements: the viewer is not mounted");
    return viewerImpl;
  };

  const {
    showDynamic,
    fileVersionId,
    takeOffItemId,
    estimateId,
    source,
    quantityTakeoffToolbarVisibility,
    takeOffIncludedMeasuremnt,
    setBtnState,
    deleteMeasurementFromTakeOff,
    handleRowSelection,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- boundary brand for the three ids only, see MeasurementsPropsInternal: every request built from them is re-validated by `objectIdString` in the backend route schema
  } = props as MeasurementsPropsInternal;

  const params = useParams<{ versionId: string }>();

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [duplicateModalAndData, setDuplicateModalAndData] = useState<{
    show: boolean;
    data: Partial<MeasurementGridRow>;
  }>({
    show: false,
    data: {},
  });
  const [startStop, setStartStop] = useState(false);
  const [itemsMarked, setItemsMarked] = useState<string[]>([]);
  const [takeoffsList, setTakeoffsList] = useState<
    NonNullable<Awaited<ReturnType<typeof ACTIONS.getTakeoffsList>>["data"]["result"]>
  >([]);
  // Holds the takeoff ids ticked in the "Add to takeoffs" list; validated where they are read back
  // off the DOM (`checkboxClicked`), so the attach request below needs no assertion.
  const [takeoffsChecked, setTakeoffsChecked] = useState<ObjectIdString[]>([]);
  const [showTakeoffsList, setShowTakeoffsList] = useState(false);
  const [allMeasurementTags, setAllMeasurementTags] = useState<string[]>([]);
  const [showNewTakeoffModal, setShowNewTakeoffModal] = useState(false);
  const [enableToolbarButtons, setEnableToolbarButtons] = useState(false);
  // `data` also takes `undefined`: "Create Manual Measurement" opens the modal with no seed row.
  const [manualMeasurementModal, setManualMeasurementModal] = useState<{
    show: boolean;
    data: ACTIONS.ManualMeasurementState | null | undefined;
  }>({
    show: false,
    data: null,
  });
  const [editingRowValue, setEditingRowValue] = useState<EditingRowValue>({});
  // measurementId -> whether that measurement's markup is currently drawn in the viewer.
  const [displayMeasurements, setDisplayMeasurements] = useState<Record<string, boolean>>({});
  const [selectMeasurementAndPage, setSelectMeasurementAndPage] = useState(false);
  const itemsMarkedRef = useRef<string[]>([]);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const urlSearchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const getInitialSheetId = () => {
    let initialSheetId: SheetId | null = null;
    if (source == ACTIONS.measurementSources.MEASUREMENTS) {
      initialSheetId = -1;
      if (
        getIsViewerDependencyLoaded({ isModelStructureReady, isSettingsApplied, viewerImpl, hwv: CubeGlobal.hwv }) &&
        getViewer().hwv.sheetManager.getActiveSheetId()
      ) {
        initialSheetId = getViewer().hwv.sheetManager.getActiveSheetId();
      } else if (urlSearchParams.get("measurementId")) {
        initialSheetId = null;
      }
    }
    return initialSheetId;
  };
  const [currentSheetId, setCurrentSheetId] = useState(getInitialSheetId());
  const currentSheetIdRef = useRef(currentSheetId);
  const isInitialMarkupLoadDone = useRef(false);

  // Carried on the tab descriptors below and never dereferenced here, so the payload stays opaque.
  const typePerimeterRef = useRef<unknown>(null);
  const typeSurfaceAreaRef = useRef<unknown>(null);
  const typeCircumferenceRef = useRef<unknown>(null);
  const typeLengthSegmentsRef = useRef<unknown>(null);

  const typePerimeter = { text: "Perimeter", value: "perimeter", reference: typePerimeterRef };
  const typeSurfaceArea = { text: "Surface Area", value: "surfaceArea", reference: typeSurfaceAreaRef };
  const typeCircumference = { text: "Circumference", value: "circumference", reference: typeCircumferenceRef };
  const typeLengthSegments = { text: "Length Segments", value: "lengthSegments", reference: typeLengthSegmentsRef };

  const derivedMeasurementsBaseValue = {
    [typePerimeter.value]: [],
    [typeSurfaceArea.value]: [],
    [typeCircumference.value]: [],
    [typeLengthSegments.value]: [],
  };
  const emptyDerivedMeasurements = {
    data: derivedMeasurementsBaseValue,
    selectedRecords: derivedMeasurementsBaseValue,
  };
  const [derivedMeasurementOptions, setDerivedMeasurementOptions] = useState<DerivedMeasurementOption[]>([]);
  const [activeDerivedMeasurementTab, setActiveDerivedMeasurementTab] = useState<string | null>(null);
  const [showDeriveMeasurementsModal, setShowDeriveMeasurementsModal] = useState(false);
  const [derivedMeasurementSelected, setDerivedMeasurementSelected] = useState(false);
  const flagToCheckRowSelectionRef = useRef(false);

  const HoopsModalStatus = useSelector((state: RootState) => state.HoopsModalStatus);
  const takeoffData = useSelector((state: RootState) => state.takeoffData);
  const isMeasurementStarted = useSelector((state: RootState) => state.isMeasurementStarted);
  const measurementsList = useSelector((state: RootState) => state.measurementsList);
  const measurementsModalStatus = useSelector((state: RootState) => state.measurementsModalStatus);
  const estimateData = useSelector((state: RootState) => state.estimateData);
  const estimateError = useSelector((state: RootState) => state.estimateError);
  const estimateFieldsNeedToRefresh = useSelector((state: RootState) => state.estimateFieldsNeedToRefresh);
  const filesData = useSelector((state: RootState) => state.filesData);
  const fileversionIdAndNodeOffsetIdMap = useSelector((state: RootState) => state.fileversionIdAndNodeOffsetIdMap);

  const actionPermissionAllowed = ACTIONS.isEstimateRequiredPermission(estimateData, RESOURCE_MODIFY.id);

  useEffect(() => {
    if (selectMeasurementAndPage && dataGrid.current && measurementsList.length) {
      const measurementId = urlSearchParams.get("measurementId");
      const index = measurementsList.findIndex((ele) => ele.measurementId == measurementId);
      if (index !== -1) {
        dataGrid.current!.slickGrid.setSelectedRows([index]);
      }
    }
  }, [selectMeasurementAndPage]);

  const localRef = useRef<MeasurementsGridRef | null>(null);
  // The forwarded ref is exposed as `unknown` (see the note above the component), so it cannot be
  // read back as the grid instance. The grid is handed this view of `localRef` instead: `current` is
  // stored on — and read from — the typed local ref, and mirrored out to whatever the caller passed,
  // callback ref as well as object ref.
  const dataGrid = useMemo<MutableRefObject<MeasurementsGridRef | null>>(
    () => ({
      get current() {
        return localRef.current;
      },
      set current(instance: MeasurementsGridRef | null) {
        localRef.current = instance;
        if (typeof ref === "function") {
          ref(instance);
        } else if (ref) {
          ref.current = instance;
        }
      },
    }),
    [ref],
  );
  const startStopRef = useRef(startStop);
  const displayMeasurementsRef = useRef<Record<string, boolean>>({});
  const measurementsListRef = useRef<MeasurementGridRow[]>([]);
  // Only ever reset to null here; the derive grid keeps its own ref.
  const derivedMeasurementsGrid = useRef<unknown>(null);
  const takeoffDataRef = useRef(takeoffData);
  const derivedMeasurementsRef = useRef<{
    data: DerivedMeasurementBuckets;
    selectedRecords: DerivedMeasurementBuckets;
  }>(emptyDerivedMeasurements);
  const takeoffsModalStatusRef = useRef(HoopsModalStatus.Takeoffs);
  const measurementsModalStatusRef = useRef(measurementsModalStatus);
  const activeDerivedMeasurementTabRef = useRef<string | null>(activeDerivedMeasurementTab);

  const isFilterAllowed =
    source === ACTIONS.measurementSources.MEASUREMENTS || source === ACTIONS.measurementSources.ESTIMATE;
  // External entry points: the estimate-version id arrives either as the `:versionId` route param or
  // as the `versionId` query param. Neither has passed through the type system, so both are
  // validated here — `objectIdString` is the boundary check the brand exists for.
  const routeVersionIdParam = params.versionId;
  const queryVersionIdParam = urlSearchParams.get("versionId");
  const routeVersionId = objectIdString.safeParse(routeVersionIdParam).data;
  const queryVersionId = objectIdString.safeParse(queryVersionIdParam).data;
  const estimateVersionId = routeVersionId ?? queryVersionId;
  // Any versionId in the URL means an historical estimate version is open and is read-only. Gated on
  // the raw params, not the validated ids, so a malformed one still locks the panel rather than
  // silently editing the latest version.
  const isVersionMode = Boolean(routeVersionIdParam || queryVersionIdParam);
  const fileRemovedTooltipContent = t("File from which the measurement was taken is not available.");

  const initialRender = useRef(true);

  const [gridQuery] = useState(
    source == ACTIONS.measurementSources.MEASUREMENTS
      ? null
      : {
          params: {
            estimateId,
            fileVersionId: takeOffIncludedMeasuremnt ? undefined : fileVersionId,
            takeOffItemId,
            versionId: estimateVersionId,
            showDynamic,
            takeOffIncludedMeasuremnt,
          },
        },
  );

  const activateOperator = function (operatorId: OperatorId, opIndex = 1) {
    getViewer().activateOperator(operatorId, opIndex);
  };

  const fetchMeasurements = useCallback(async () => {
    // This caller used to send an explicit `takeOffItemId: null`, which the list service treats
    // exactly like an absent one (`if (takeOffItemId)`, measurements.service.ts:1186) — omitted so
    // the params match `getMeasurements`' own parameter type instead of being asserted into it.
    const params: Parameters<typeof ACTIONS.getMeasurements>[0] = {
      estimateId,
      fileVersionId,
      versionId: estimateVersionId,
    };
    if (currentSheetIdRef.current) {
      params.sheetId = currentSheetIdRef.current;
    }
    dispatch(ACTIONS.getMeasurements(params));
  }, [estimateId, fileVersionId, estimateVersionId]);

  useEffect(() => {
    if (estimateData._id) {
      // `estimateDataSchema` does not declare `tags`, so it reaches here through the schema's
      // passthrough index signature as `unknown`. The pool is a list of tag strings — kept only
      // where the response really carries one, rather than asserted.
      const tags = estimateData.tags;
      setAllMeasurementTags(Array.isArray(tags) ? tags.filter((tag) => typeof tag === "string") : []);
    }
  }, [estimateData.tags, estimateData._id]);

  // `data` is a takeoff (its style fields) — either the redux `takeoffData` or a measurement's
  // embedded `takeOffs[0]`.
  const loadMarkupStyles = (viewerMarkup: CubeMeasureMarkup, data: Partial<TakeoffMarkupStyle>) => {
    const { lineThickness, fillOpacity, symbolSize, countSymbol, lineColor: line_color, fillColor: fill_color } = data;
    const lineColor = line_color && ACTIONS.prepareMarkupColor(line_color);
    const fillColor = fill_color && ACTIONS.prepareMarkupColor(fill_color);
    // The `hwv` key that used to be passed here is gone: `loadMarkupStylesViewer` has no such
    // parameter (it reads `CubeGlobal.hwv` itself), so it was ignored and only forced the whole
    // argument to be asserted into the parameter type.
    ACTIONS.loadMarkupStylesViewer({
      viewerImpl,
      viewerMarkup,
      lineThickness,
      fillOpacity,
      symbolSize,
      countSymbol,
      lineColor,
      fillColor,
      isModelStructureReady,
      isSettingsApplied,
    });
  };

  const styleMarkups = (measurement: MeasurementGridRow, getAllMeasurements: CubeMeasureMarkup[]) => {
    // The measurement is drawn with the appearance of the first takeoff it is attached to;
    // `measurementItemSchema` now declares those style fields on the embed, so it is read, not cast.
    const takeoffStyle = measurement.takeOffs?.[0];
    if (!takeoffStyle) return;
    const viewerMarkup = getAllMeasurements.find((record) => record.cubeUniqueId === measurement.measurementId);
    if (!viewerMarkup) return;

    loadMarkupStyles(viewerMarkup, takeoffStyle);
  };

  const loadAllMarkups = async () => {
    const markups = measurementsListRef.current
      .filter((record) => record.markup !== "{}")
      .map((record) => JSON.parse(record.markup!));
    const measurementIds = measurementsListRef.current.map((record) => record.measurementId);
    const obj: Record<string, boolean> = {};
    measurementIds.map((record) => {
      obj[record!] = true;
    });
    setDisplayMeasurements(obj);
    await getViewer().loadMarkups(markups);
    const getAllMeasurements: CubeMeasureMarkup[] = getViewer().hwv.measureManager.getAllMeasurements();
    measurementsListRef.current.map((measurement) => styleMarkups(measurement, getAllMeasurements));

    const measurement3D = measurementsListRef.current.filter(
      (measurement) => measurement.markup == "{}" && measurement.nature !== "Static" && measurement.elements,
    );
    if (measurement3D.length && filesData[fileVersionId]) {
      const mapper = getFileversionIdAndNodeOffsetIdMap(fileVersionId);
      measurement3D.map((measurement) => {
        handle3DElementSelectCallback(measurement.elements!, mapper);
      });
    }
  };

  const filterMeasurementsForUnmount = (measurementsData: MeasurementGridRow[]) => {
    if (takeoffsModalStatusRef.current) {
      if (takeoffsModalStatusRef.current.currentStage === "Takeoffs") {
        measurementsData = measurementsData.filter((record) => !record.takeOffs?.length);
      } else if (takeoffsModalStatusRef.current.currentStage === "Takeoff_Detail") {
        measurementsData = measurementsData.filter(
          (record) =>
            !record.takeOffs?.find((takeoff) => takeoff.takeOffItemId == takeoffDataRef.current.takeOffItemId),
        );
      }
    }
    return measurementsData;
  };

  const removeAllMarkupsFromViewer = ({ unmount = false }: { unmount?: boolean }) => {
    if (getIsViewerDependencyLoaded({ isModelStructureReady, isSettingsApplied, viewerImpl, hwv: CubeGlobal.hwv })) {
      let measurementsData = measurementsListRef.current;
      setDisplayMeasurements(
        measurementsData.reduce<Record<string, boolean>>((prev, curr) => {
          prev[curr.measurementId!] = false;
          return prev;
        }, {}),
      );
      const getAllMeasurements: CubeMeasureMarkup[] = getViewer().hwv.measureManager.getAllMeasurements();

      if (unmount) measurementsData = filterMeasurementsForUnmount(measurementsData);
      const markupDataIds = new Set(measurementsData.map((record) => record.measurementId));
      const markupsToRemove = getAllMeasurements.filter((record) => markupDataIds.has(record.cubeUniqueId));
      for (const markup of markupsToRemove) {
        getViewer().hwv.measureManager.removeMeasurement(markup);
      }

      const measurement3D = measurementsData.filter(
        (measurement) => measurement.markup == "{}" && measurement.nature !== "Static" && measurement.elements,
      );
      if (measurement3D.length && filesData[fileVersionId]) {
        const mapper: NodeOffsetMap = getFileversionIdAndNodeOffsetIdMap(fileVersionId);
        measurement3D.map((data) => {
          const element = data.elements!;
          if (getViewer().hwv.selectionManager.isNodeSelected(element.nodeId! + mapper[element.fileVersionId!]!)) {
            const selectedSelectionItem: { getNodeId(): number }[] = getViewer().hwv.selectionManager.getResults();
            const selectionItem = selectedSelectionItem.find(
              (item) => item.getNodeId() == element.nodeId! + mapper[element.fileVersionId!]!,
            );
            selectionItem && getViewer().hwv.selectionManager.remove(selectionItem);
          }
        });
      }
    }
  };

  const reloadMeasurements = (fetchData: boolean) => {
    if (source === ACTIONS.measurementSources.MEASUREMENTS && fetchData) fetchMeasurements();
    else if (source === ACTIONS.measurementSources.TAKEOFFS) {
      dispatch(
        ACTIONS.getTakeoffData({
          takeOffItemId,
          estimateId,
          versionId: queryVersionId,
        }),
      );
      dataGrid.current!.refresh!();
    }
  };

  const saveMeasurements = async (measurement: CubeMeasureMarkup, fetchData: boolean) => {
    const { cubeUniqueId, _name, _measurementValue, calScale, _unitMultiplier } = measurement;
    const measurementDetails = ACTIONS.getMeasurementDetails(_name);
    if (!measurementDetails.type) return;

    // Reassigned from the markup's nested list to the mapped list and then to its JSON string, so
    // the local is declared as the union of the three shapes it holds.
    let nestedMeasurements: NestedMeasurementStage = measurement.getNestedMeasurements
      ? measurement.getNestedMeasurements()
      : null;

    nestedMeasurements = nestedMeasurements
      ? (nestedMeasurements as NonNullable<ReturnType<NonNullable<CubeMeasureMarkup["getNestedMeasurements"]>>>).map(
          (record, index: number) => ({
            type: record.type.value,
            originalValue: record.value,
            alteredValue: record.value,
            name: record.name === "" ? `Measurement ${index + 1}` : record.name,
          }),
        )
      : null;

    nestedMeasurements = nestedMeasurements
      ? JSON.stringify({
          data: nestedMeasurements,
          total: {
            originalValue: _measurementValue * _unitMultiplier,
            alteredValue: _measurementValue * _unitMultiplier,
            name: "Measurement",
            type: measurementDetails.type,
          },
        })
      : null;

    const scale = calScale ? `1:${Number.parseFloat(Number.parseFloat(calScale).toFixed(3))}` : "1:1";

    const markupData = {
      measurementId: cubeUniqueId,
      name: measurementDetails.name,
      type: measurementDetails.type,
      quantity: _measurementValue * _unitMultiplier,
      nestedMeasurements,
      takeOffItemId,
      scale,
      markup: JSON.stringify(measurement.toJson()),
      fileVersionId,
      estimateId,
      // No active sheet is the ABSENCE of a sheet id, not a null one: `createMeasurementRequest`
      // declares `sheetId: z.number().optional()`, which rejects null, so sending the null this ref
      // holds when the panel is opened from a measurement deep-link failed the request outright.
      // Omitted instead, which is what the service already defaults to -1 for.
      sheetId: currentSheetIdRef.current ?? undefined,
    };

    await ACTIONS.createMeasurement(markupData);
    reloadMeasurements(fetchData);
  };

  const destroyMeasurement = async () => {
    const selectedData: MeasurementGridRow[] = dataGrid
      .current!.slickGrid.getSelectedRows()
      .map((data) => dataGrid.current!.slickGrid.getDataItem(data));
    if (source === ACTIONS.measurementSources.TAKEOFFS) {
      // Only the takeoff surfaces pass this callback down, and only they reach this branch; the
      // existing `response &&` below is already the guard for it not having run.
      const response = await deleteMeasurementFromTakeOff?.({
        measurementIds: selectedData.map((item) => item.measurementId!),
      });
      if (response && get(dataGrid, "current")) {
        dataGrid.current!.refresh!();
      }
    } else {
      if (!itemsMarked.length) return;

      const data = { key: itemsMarked, estimateId };
      const response = await ACTIONS.deleteMeasurement(data);
      if (response.data.status) {
        for (const measurementId of itemsMarked) {
          const measurement = getMeasurementFromId(measurementId);
          measurement && getViewer().hwv.measureManager.removeMeasurement(measurement);
        }

        if (get(dataGrid, "current")) {
          dataGrid.current!.slickGrid.setSelectedRows([]);
          setItemsMarked([]);
          setEnableToolbarButtons(false);
          reloadMeasurements(true);
          setDisplayMeasurements((prev) => {
            const copyObj = { ...prev };
            for (const id of itemsMarked) {
              delete copyObj[id];
            }
            return copyObj;
          });
        }
      }

      const allTakeoffIds = selectedData
        .filter((item) => item.takeOffs?.length)
        .flatMap((item) => item.takeOffs!.map((takeoff) => takeoff.takeOffItemId!));
      if (allTakeoffIds.length) {
        const setData = ACTIONS.prepareEstimationFieldNeedToRefresh({
          HoopsModalStatus,
          takeoffs: allTakeoffIds,
          estimateFieldsNeedToRefresh,
        });
        dispatch(setEstimateFieldsNeedtoRefresh(setData));
      }
    }
  };

  const showAllTools = () =>
    props.handleToolsVisibility!({
      linear: true,
      area: true,
      volume: true,
      count: true,
    });

  const hideAllTools = () =>
    props.handleToolsVisibility!({
      linear: false,
      area: false,
      volume: false,
      count: false,
    });

  const showSpecificTool = () => {
    const toolsState: MeasurementToolsVisibility = {
      linear: false,
      area: false,
      volume: false,
      count: false,
    };
    // The takeoff type names one of the four keys above; asserted inline for the dynamic key.
    (toolsState as Record<string, boolean>)[props.takeoffType!.toLowerCase()] = true;
    props.handleToolsVisibility!(toolsState);
  };

  const startStopMeasurement = () => {
    const currentState = startStop;
    if (!currentState && !isMeasurementStarted.status) {
      setStartStop(!startStop);
      dispatch(setIsMeasurementStarted({ status: true }));
      props.quantityTakeoffToolbarVisibility!(true);
      if (takeOffItemId) {
        showSpecificTool();
      } else {
        showAllTools();
      }
      return;
    }

    if (currentState && isMeasurementStarted.status) {
      setStartStop(!startStop);
      props.quantityTakeoffToolbarVisibility!(false);
      activateOperator(OperatorId.Select);
      dispatch(setIsMeasurementStarted({ status: false }));
      hideAllTools();
    }
  };

  const headerEyeIconClickHandler = (e: React.MouseEvent) => {
    if (getIsViewerDependencyLoaded({ isModelStructureReady, isSettingsApplied, viewerImpl, hwv: CubeGlobal.hwv })) {
      const numberOfMeasurements = measurementsListRef.current.length;
      const numberOfVisibleMeasurements = Object.values(displayMeasurements).filter(Boolean).length;

      if (!numberOfMeasurements) return;
      removeAllMarkupsFromViewer({ unmount: false });
      if (numberOfMeasurements !== numberOfVisibleMeasurements) loadAllMarkups();

      e.stopPropagation();
      e.preventDefault();
    } else {
      dispatch(dispatchModelLoadingToast());
    }
  };

  const finishDeriveMeasurements = () => {
    setActiveDerivedMeasurementTab(null);
    setDerivedMeasurementSelected(false);
    setShowDeriveMeasurementsModal(false);

    derivedMeasurementsGrid.current = null;
    activeDerivedMeasurementTabRef.current = null;
    derivedMeasurementsRef.current = emptyDerivedMeasurements;
  };

  const saveDerivedMeasurements = async (e: React.MouseEvent) => {
    e.preventDefault();

    const allDerivedMeasurements = Object.values(derivedMeasurementsRef.current.selectedRecords).flat();
    for (let i = 0; i < allDerivedMeasurements.length; i++) {
      const derivedMeasurement = allDerivedMeasurements[i]!;
      const fetchData = i === allDerivedMeasurements.length - 1;

      getViewer().hwv.measureManager.addMeasurement(derivedMeasurement);
      getViewer().hwv.measureManager.finalizeMeasurement(derivedMeasurement);

      await handleSaveMeasurement(derivedMeasurement, fetchData);
    }

    setDerivedMeasurementOptions([]);
    finishDeriveMeasurements();
  };

  /**
   * Normalises what the four `derive*` markup methods return (one markup or a list) into the derive
   * grid's rows. Numbering them is what makes them rows: `id` is the row key the grid requires, and
   * assigning it here is what the return type states, instead of the buckets being asserted later.
   */
  const generateDriveMeasurementData = (
    deriveMeasurement: CubeMeasureMarkup | CubeMeasureMarkup[],
  ): DerivedMeasurementMarkup[] => {
    const markups = Array.isArray(deriveMeasurement) ? deriveMeasurement : [deriveMeasurement];
    return markups.map((markup, index) => Object.assign(markup, { id: index }));
  };

  const beginDeriveMeasurements = () => {
    let perimeter: DerivedMeasurementMarkup[] = [];
    let surfaceArea: DerivedMeasurementMarkup[] = [];
    let circumference: DerivedMeasurementMarkup[] = [];
    let lengthSegments: DerivedMeasurementMarkup[] = [];

    const measurement = getMeasurementFromId(itemsMarked[0]!);

    // 3d case
    if (measurement) {
      if (measurement.derivePerimeterMeasurement) {
        perimeter = generateDriveMeasurementData(measurement.derivePerimeterMeasurement());
      }
      if (measurement.deriveSurfaceAreaMeasurement) {
        surfaceArea = generateDriveMeasurementData(measurement.deriveSurfaceAreaMeasurement());
      }
      if (measurement.deriveCircumferenceMeasurement) {
        circumference = generateDriveMeasurementData(measurement.deriveCircumferenceMeasurement());
      }
      if (measurement.deriveLengthSegmentsMeasurement) {
        lengthSegments = generateDriveMeasurementData(measurement.deriveLengthSegmentsMeasurement());
      }
    }

    setShowDeriveMeasurementsModal(true);
    setActiveDerivedMeasurementTab(derivedMeasurementOptions[0]!.value);
    activeDerivedMeasurementTabRef.current = derivedMeasurementOptions[0]!.value;
    derivedMeasurementsRef.current.data = {
      lengthSegments,
      circumference,
      perimeter,
      surfaceArea,
    };
  };

  const duplicateMeasurementClickHandler = () => {
    const seletedRecord: MeasurementGridRow[] = dataGrid
      .current!.slickGrid.getSelectedRows()
      .map((data) => dataGrid.current!.slickGrid.getDataItem(data));
    setDuplicateModalAndData({ show: true, data: seletedRecord[0]! });
  };
  const closeDuplicateMeasurementModal = () => {
    setDuplicateModalAndData({ show: false, data: {} });
  };

  const copyMeasurement = async (linkTakeoffs: boolean) => {
    const data = {
      measurementId: duplicateModalAndData.data.measurementId!,
      estimateId,
      linkTakeoffs,
    };
    const response = await ACTIONS.duplicateMeasurement(data);
    if (response.data.status) {
      if (
        source === ACTIONS.measurementSources.MEASUREMENTS &&
        duplicateModalAndData.data.takeOffs?.length &&
        linkTakeoffs
      ) {
        const setData = ACTIONS.prepareEstimationFieldNeedToRefresh({
          HoopsModalStatus,
          takeoffs: duplicateModalAndData.data.takeOffs!.map((takeoff) => takeoff.takeOffItemId!),
          estimateFieldsNeedToRefresh,
        });
        dispatch(setEstimateFieldsNeedtoRefresh(setData));
      }
      fetchMeasurements();
      closeDuplicateMeasurementModal();
    } else {
      dispatch(
        showToastNotification({
          content: untranslated(response.data.errorMessage ?? "An error occurred"),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  /**
   * The tag picker's `change` payload is declared `{ value?: unknown }` — it is shared with pickers
   * whose datasource holds option objects. This one's datasource is `allMeasurementTags`, a string
   * array, and `allowCustomValue` adds typed-in strings, so the selection is read (not asserted)
   * back to the string list `updateMeasurementTags` takes.
   */
  const readSelectedTags = (payload: { value?: unknown }): string[] =>
    Array.isArray(payload.value) ? payload.value.filter((tag) => typeof tag === "string") : [];

  const handleTagsChange = async (selectedTags: string[], measurementId: string) => {
    await ACTIONS.updateMeasurementTags({
      measurementId,
      tags: selectedTags,
      estimateId,
    });

    const elements = structuredClone(measurementsListRef.current);
    const measurementsUpdated = elements.map((record) => {
      const { tags } = record;

      if (record.measurementId === measurementId) {
        if (tags) {
          record.tags = [...record.tags!, selectedTags.at(-1)!];
        } else {
          record.tags = selectedTags;
        }
      }

      return record;
    });
    measurementsListRef.current = elements;

    dispatch(ACTIONS.setMeasurements(measurementsUpdated));
    const getEstimateParams: GetEstimateDataProp = { estimateId };
    if (estimateVersionId) {
      getEstimateParams.versionId = estimateVersionId;
    }
    dispatch(getEstimateData(getEstimateParams));
  };

  const handleShowTakeoffsList = (value: boolean) => setShowTakeoffsList(value);

  const closeTakeoffModal = () => setShowNewTakeoffModal(false);

  const addToTakeoffsClickHandler = () => {
    const currentState = showTakeoffsList;
    handleShowTakeoffsList(!showTakeoffsList);
    setTakeoffsChecked([]);
    if (!currentState) fetchTakeoffsList();
  };

  const attachMeasurementsToTakeoffs = async () => {
    if (!takeoffsChecked.length) {
      return;
    }

    const data = takeoffsChecked.map((takeoffChecked) => ({
      takeOffItemId: takeoffChecked,
      measurementIds: itemsMarked,
    }));
    await ACTIONS.addMeasurementsToTakeoffs({ data, estimateId });
    const setData = ACTIONS.prepareEstimationFieldNeedToRefresh({
      HoopsModalStatus,
      takeoffs: takeoffsChecked,
      estimateFieldsNeedToRefresh,
    });
    dispatch(setEstimateFieldsNeedtoRefresh(setData));

    setShowTakeoffsList(false);
    fetchMeasurements();
  };

  const checkboxClicked = (event: React.MouseEvent<HTMLInputElement>) => {
    // `currentTarget` is the checkbox the handler is bound to (React types `target` as the bare
    // `EventTarget`). Its `name` is the takeoff's own `takeOffItemId`, echoed back through the DOM
    // as a plain string, so it is validated here rather than asserted at the attach request.
    const takeoffCheckedItemId = objectIdString.safeParse(event.currentTarget.name).data;
    if (!takeoffCheckedItemId) return;
    if (event.currentTarget.checked) setTakeoffsChecked((previous) => [...previous, takeoffCheckedItemId]);
    else setTakeoffsChecked((previous) => previous.filter((element) => element !== takeoffCheckedItemId));
  };

  const fetchTakeoffsList = async () => {
    const versionId = source == ACTIONS.measurementSources.MEASUREMENTS ? queryVersionId : routeVersionId;
    const markedMeasurement = measurementsList.find((val) => val.measurementId === itemsMarked[0]);
    const response = await ACTIONS.getTakeoffsList({
      estimateId,
      // `unit` and its `type.id` are `.optional()` on `measurementItemSchema`, and the lookup can
      // miss; `type` is an optional filter on the list, so an unknown type simply does not filter.
      type: markedMeasurement?.unit?.type?.id,
      versionId,
    });
    if (response.data.status) setTakeoffsList(response.data.result ?? []);
  };

  const startNewTakeoff = () => {
    setShowNewTakeoffModal(true);
    setShowTakeoffsList(false);
    setTakeoffsChecked([]);
  };

  const handle3DElementSelectCallback = (element: MeasurementNodeElement, mapper: NodeOffsetMap) => {
    try {
      const selectedNode = Selection.SelectionItem.create(element.nodeId! + mapper[element.fileVersionId!]!);
      getViewer().hwv.selectionManager.add(selectedNode, false); // false so that selection callback can be triggered and metadata can be viewed
    } catch (e) {
      console.log(e);
      Promise.reject(e);
    }
  };

  const focusMeasurements = async (measurements: MeasurementGridRow[], mapper: NodeOffsetMap) => {
    const measurements3D = measurements.filter(
      (data) =>
        displayMeasurementsRef.current[data.measurementId!] &&
        data.markup == "{}" &&
        data.nature !== "Static" &&
        data.elements,
    );
    const markUpMeasurements = measurements
      .filter((data) => data.markup !== "{}")
      .map((measurement) => getMeasurementFromId(measurement.measurementId!))
      .filter(Boolean);

    const bounding: HoopsBox | undefined = getAggregateMeasurementBoundingBox({
      measurements: markUpMeasurements,
    });

    const nodeIds = measurements3D.map((data) => {
      const element = data.elements!;
      return element.nodeId! + mapper[element.fileVersionId!]!;
    });

    let parentBox;
    if (markUpMeasurements.length && bounding) {
      parentBox = bounding.copy();
    }

    if (nodeIds.length) {
      const dynM = await getViewer().hwv.model.getNodesBounding(nodeIds);
      if (parentBox) {
        parentBox.addBox(dynM);
      } else {
        parentBox = dynM.copy();
      }
    }

    if (parentBox) {
      getViewer().hwv.view.fitBounding(parentBox, 300);
    }
  };

  const buttonStateHandler = (totalRowsSelected: number) => {
    if (totalRowsSelected >= 1) {
      setEnableToolbarButtons(true);
    } else {
      setEnableToolbarButtons(false);
      setShowTakeoffsList(false);
    }
  };

  const selectingEventsInViewer = async () => {
    const selectedRecords = dataGrid
      .current!.dataView.getAllSelectedItems()
      .filter(Boolean)
      .filter((record) => !record.__isDummy);
    buttonStateHandler(selectedRecords.length);

    if (source == ACTIONS.measurementSources.TAKEOFFS) return;

    if (!getIsViewerDependencyLoaded({ isModelStructureReady, isSettingsApplied, viewerImpl, hwv: CubeGlobal.hwv }))
      return;
    const needToSelectRecords = selectedRecords.filter(
      (record) => !itemsMarkedRef.current.includes(record.measurementId!),
    );
    // KNOWN BUG, preserved verbatim: `find` can miss, so this list can hold `undefined` entries —
    // `dataList` below then dereferences them unguarded. Declared as the nullable row for that reason.
    const needToDeselectRecords: (MeasurementGridRow | undefined)[] = itemsMarkedRef.current
      .filter((id) => !selectedRecords.some((item) => item.measurementId == id))
      .map((id) => measurementsListRef.current.find((item) => item.measurementId == id));
    if (!needToSelectRecords.length && !needToDeselectRecords.length) {
      return;
    }

    const dataList = needToSelectRecords.length ? needToSelectRecords : needToDeselectRecords;
    const mapper: NodeOffsetMap = getFileversionIdAndNodeOffsetIdMap(fileVersionId);
    const measurements: (CubeMeasureMarkup | undefined)[] = [];

    for (const sel of dataList) {
      if (sel!.markup !== "{}") {
        measurements.push(getMeasurementFromId(sel!.measurementId!));
      }
    }

    if (needToSelectRecords.length) {
      // `dataList` IS `needToSelectRecords` in this branch (the ternary above), read through its own
      // name so the rows keep the non-nullable type the deselect list does not have.
      needToSelectRecords
        .filter(
          (element) =>
            displayMeasurementsRef.current[element.measurementId!] &&
            element.markup == "{}" &&
            element.nature !== "Static" &&
            element.elements,
        )
        .map((element) => handle3DElementSelectCallback(element.elements!, mapper));
      focusMeasurements(needToSelectRecords, mapper);
    } else if (needToDeselectRecords.length) {
      if (measurements.length) {
        for (const measurement of measurements) {
          measurement?.unSelect();
        }
        getViewer().hwv.markupManager.refreshMarkup(getViewer().hwv.view);
      }
    }

    setItemsMarked(selectedRecords.map((record) => record.measurementId!));
  };

  const selectingEvents = () => {
    switch (source) {
      case ACTIONS.measurementSources.MEASUREMENTS:
        if (!openAsPopup) {
          selectingEventsInViewer();
        }
        break;
      case ACTIONS.measurementSources.TAKEOFFS:
        if (takeOffIncludedMeasuremnt && !takeoffData.isSystemGenerated) {
          selectingEventsInViewer();
        } else {
          if (setBtnState) {
            // `getAllSelectedItems()` is DECLARED `T[]`, but SlickDataView builds the array from
            // `getItemById(id)` for every id still selected, so a selection kept across a refresh
            // yields `undefined` holes (SlickGrid1/index.tsx:4116). Read at a type that admits them,
            // which is what makes the filter below meaningful.
            const selectedItems: (MeasurementGridRow | undefined)[] = dataGrid.current!.dataView.getAllSelectedItems();
            const selectedRecords = selectedItems.filter((record) => record && !record.__isDummy);
            setBtnState(selectedRecords.length > 0);
          }
        }
        break;

      case ACTIONS.measurementSources.REFERENCE_MEASUREMENT:
        // Only AddReferenceList renders this panel with the REFERENCE_MEASUREMENT source, and it is
        // the caller that supplies the callback.
        handleRowSelection?.();
        break;
      default:
        break;
    }
  };

  const derivedMeasurementsSelectingEvents = (
    selectedRecords: ComponentProps<typeof DeriveMeasurements>["dataSource"],
  ) => {
    if (flagToCheckRowSelectionRef.current) {
      flagToCheckRowSelectionRef.current = false;
    } else {
      const activeTab = activeDerivedMeasurementTabRef.current!;
      // The derive grid's row type states only what the grid itself needs, so the rows it hands back
      // have lost the markup API `saveDerivedMeasurements` calls. Resolve them back to the markups
      // this tab was given as `dataSource` — the same objects, matched on the `id`
      // `generateDriveMeasurementData` assigned — instead of asserting the grid rows back up.
      const selectedIds = new Set(selectedRecords.map((record) => record.id));
      derivedMeasurementsRef.current.selectedRecords = {
        ...derivedMeasurementsRef.current.selectedRecords,
        [activeTab]: (derivedMeasurementsRef.current.data[activeTab] ?? []).filter((markup) =>
          selectedIds.has(markup.id),
        ),
      };
      setDerivedMeasurementSelected(Object.values(derivedMeasurementsRef.current.selectedRecords).flat().length > 0);
    }
  };

  const selectMeasurement = (measurement: MeasurementGridRow) => {
    if (measurement.markup !== "{}") {
      getViewer().loadMarkups([JSON.parse(measurement.markup!)]);
      const getAllMeasurements: CubeMeasureMarkup[] = getViewer().hwv.measureManager.getAllMeasurements();
      const viewerMarkup = getAllMeasurements.find((record) => record.cubeUniqueId === measurement.measurementId);
      const takeoffStyle = measurement.takeOffs?.[0];
      if (viewerMarkup && takeoffStyle) loadMarkupStyles(viewerMarkup, takeoffStyle);
    } else if (
      // `markup` is already narrowed to "{}" by the branch above, so only the other three matter here.
      measurement.nature != "Static" &&
      measurement.elements &&
      Object.hasOwn(fileversionIdAndNodeOffsetIdMap, fileVersionId)
    ) {
      handle3DElementSelectCallback(measurement.elements!, fileversionIdAndNodeOffsetIdMap);
    }
  };

  const deSelectMeasurement = (measurement: MeasurementGridRow) => {
    if (measurement.markup !== "{}") {
      const getAllMeasurements: CubeMeasureMarkup[] = getViewer().hwv.measureManager.getAllMeasurements();
      const markupsToRemove = getAllMeasurements.filter((record) => record.cubeUniqueId === measurement.measurementId);
      for (const markup of markupsToRemove) {
        getViewer().hwv.measureManager.removeMeasurement(markup);
      }
    } else if (
      // `markup` is already narrowed to "{}" by the branch above, so only the other three matter here.
      measurement.nature != "Static" &&
      measurement.elements &&
      Object.hasOwn(fileversionIdAndNodeOffsetIdMap, fileVersionId)
    ) {
      const element = measurement.elements!;
      if (
        getViewer().hwv.selectionManager.isNodeSelected(
          element.nodeId! + fileversionIdAndNodeOffsetIdMap[element.fileVersionId!]!,
        )
      ) {
        const selectedSelectionItem: { getNodeId(): number }[] = getViewer().hwv.selectionManager.getResults();
        const selectionItem = selectedSelectionItem.find(
          (item) => item.getNodeId() == element.nodeId! + fileversionIdAndNodeOffsetIdMap[element.fileVersionId!]!,
        );
        selectionItem && getViewer().hwv.selectionManager.remove(selectionItem);
      }
    }
  };

  const eyeIconClickHandler = (params: MeasurementGridRow) => {
    if (getIsViewerDependencyLoaded({ isModelStructureReady, isSettingsApplied, viewerImpl, hwv: CubeGlobal.hwv })) {
      const { measurementId: rowId } = params;
      const currentlyShown = displayMeasurementsRef.current[rowId!] ? displayMeasurementsRef.current[rowId!] : false;

      if (currentlyShown) {
        setDisplayMeasurements((prev) => ({
          ...prev,
          [rowId!]: false,
        }));
        deSelectMeasurement(params);
      } else {
        setDisplayMeasurements((prev) => ({
          ...prev,
          [rowId!]: true,
        }));
        selectMeasurement(params);
      }
    } else {
      dispatch(dispatchModelLoadingToast());
    }
  };

  const recordClickInEstimateTab = (args: GridClickEvent) => {
    if (args.detail.args.cell !== 0) return;
    const rowData: MeasurementGridRow = dataGrid.current!.slickGrid.getDataItem(args.detail.args.row);
    const isParentFileActive = rowData.isParentFileActive;
    if (isParentFileActive) {
      const viewerUrl = getViewerUrl({
        fileVersionId: rowData.fileVersionId,
        estimateId,
        versionId: estimateVersionId,
        measurementId: rowData.measurementId,
        spaceId: estimateData.space?._id,
        projectId: rowData.projectId,
      });
      if (viewerUrl) {
        navigate(viewerUrl);
      }
    }
  };

  const recordClick = (params: GridClickEvent) => {
    if (source == ACTIONS.measurementSources.ESTIMATE) {
      recordClickInEstimateTab(params);
    }
  };

  const dataBound = () => {
    if (
      initialRender.current &&
      urlSearchParams.get("measurementId") &&
      source === ACTIONS.measurementSources.MEASUREMENTS &&
      get(dataGrid, "current") &&
      measurementsList.length
    ) {
      initialRender.current = false;
      setSelectMeasurementAndPage(true);
    }
  };

  const handleManualMeasurementEdit = (rowData: MeasurementGridRow) => {
    handleAddCustomMeasurement({
      name: rowData.name!,
      type: rowData.unit!.type!.name!,
      value: ACTIONS.getDisplayQuantity({
        quantity: rowData.quantity,
        factor: rowData.unit!.factor,
      }).quantity,
      // Both ids are `.optional()` on `measurementItemSchema`; the empty string is the same "nothing
      // selected" the modal's own `!manualData.unit` check already treats as incomplete.
      unit: (rowData.formula ? rowData.manualUnit : rowData.unit?.id) ?? "",
      isReferenced: !!rowData.referenceMeasurements?.length,
      references: rowData.referenceMeasurements!,
      formula: rowData.formula!,
      // BLOCKED on `src/actions/estimation.ts`: `ManualMeasurementState` declares `measurementId` as
      // an `ObjectIdString` (it is the client-minted cubeUniqueId UUID — `z.string()` in
      // measurements.schema.ts) and `takeOffs` as the full `TakeoffData[]` mirror, although the only
      // reader is ManualMeasurements.tsx's `takeOffs?.length`. Both should be widened there.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- see above: ManualMeasurementState.measurementId / .takeOffs are declared narrower than what this row carries
      measurementId: rowData.measurementId as ObjectIdString,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- see above
      takeOffs: rowData.takeOffs as ACTIONS.ManualMeasurementState["takeOffs"],
    });
  };

  const tagsTemplate: MeasurementCellTemplate = (data, value, a, b, args) => {
    const container = document.createElement("div");
    if (!args.__isDummy) {
      // Read directly rather than through `_.get`: the row is a passthrough schema type, so a dotted
      // `_.get` path now resolves to `unknown`. The optional chain also narrows away the stored
      // `null` (11 such rows on staging), which `_.get` left in the type.
      const tags = args.tags?.length ? args.tags : [];

      container.classList.add("w-full");
      const root = createRoot(container);
      root.render(
        <MultiSelectComponent
          disabled={Boolean(
            isVersionMode ||
            editingRowValue.measurementId ||
            !actionPermissionAllowed ||
            ![ACTIONS.measurementSources.ESTIMATE, ACTIONS.measurementSources.MEASUREMENTS].includes(source),
          )}
          dataSource={allMeasurementTags}
          value={tags}
          mode="Box"
          placeholder={t("Add a Tag")}
          allowCustomValue
          change={(params) => handleTagsChange(readSelectedTags(params), args.measurementId!)}
          changeOnBlur={false}
        />,
      );
    }
    return container;
  };

  const measurementTemplate: MeasurementCellTemplate = (data, val, a, b, params) => {
    let result: string | number = "";
    if (!params.__isDummy) {
      const value = ACTIONS.getDisplayQuantity({
        quantity: params.quantity! * params.multiplier!,
        factor: params.unit!.factor,
      }).quantity;
      result = ACTIONS.getEstimateNumberValueBasedOnSettings(value, estimateData, "measurement");
    }
    // `getEstimateNumberValueBasedOnSettings` hands back the raw number when the value is falsy
    // (`getNumberBasedOnSettings` returns it unformatted for 0/NaN), while a SlickGrid formatter
    // must return a string. Stringified rather than asserted: the grid renders a primitive result by
    // `String()`ing it into `textContent` anyway (slickGrid.js `applyHtmlCode`), so the cell is the
    // same either way.
    return String(result);
  };

  // Direct read, not `_.get`: `measurementItemSchema` is a passthrough object, so the row type now
  // carries a `[x: string]: unknown` index signature and lodash resolves a dotted path against it as
  // `unknown` — which a SlickGrid formatter (string | HTMLElement) cannot return. Same runtime
  // behaviour: absent unit or unit name still renders the empty string.
  const unitsTemplate: MeasurementCellTemplate = (data, value, a, b, params) => params.unit?.name ?? "";

  const takeOffsTemplate: MeasurementCellTemplate = (data, value, a, b, params) => {
    if (!params.__isDummy && get(params, "takeOffs.length")) {
      const data = params.takeOffs!.map((takeOff) => takeOff.name);
      return data.join(",");
    }
    return "";
  };

  const nameTemplate: MeasurementCellTemplate = (data, value, a, b, params) => {
    const container = document.createElement("div");
    if (!params.__isDummy) {
      container.classList.add("w-full");
      const root = createRoot(container);
      root.render(
        <div className="flex items-center justify-between w-full gap-1">
          <span className="">{params.name}</span>
          {params.isSystemGenerated && (
            <IconComponent
              name="AutoGenerated"
              position="bottom"
              toolTipContent={t("System-generated via Model to Takeoff Wizard")}
              width="1.25rem"
              height="1.25rem"
            />
          )}
        </div>,
      );
    }

    return container;
  };

  const measuredValueTemplate: MeasurementCellTemplate = (data, val, a, b, params) => {
    const container = document.createElement("div");
    if (!params.__isDummy) {
      const root = createRoot(container);
      const value = ACTIONS.getDisplayQuantity({
        quantity: params.quantity,
        factor: params.unit!.factor,
      }).quantity;

      root.render(<div>{ACTIONS.getEstimateNumberValueBasedOnSettings(value, estimateData, "measurement")}</div>);
    }
    return container;
  };

  const eyeIconTemplate: MeasurementCellTemplate = (data, value, a, b, params) => {
    const container = document.createElement("div");
    if (!params.__isDummy) {
      const root = createRoot(container);
      if (getIsViewerDependencyLoaded({ isModelStructureReady, isSettingsApplied, viewerImpl, hwv: CubeGlobal.hwv })) {
        let icon;
        if (displayMeasurements[params.measurementId!]) {
          icon = "EyeVisible";
        } else {
          icon = "EyeNotVisible";
        }
        root.render(
          <IconComponent name={icon} onClick={() => eyeIconClickHandler(params)} width="1.5rem" height="1.5rem" />,
        );
      }
    }
    return container;
  };

  const hightlightMeasurementTemplate: MeasurementCellTemplate = (data, value, a, b, params) => {
    const container = document.createElement("div");
    if (!params.__isDummy) {
      const isParentFileActive = params.isParentFileActive;
      const root = createRoot(container);
      if (isParentFileActive) {
        root.render(<OpenInViewerIcon onClick={() => {}} />);
      } else {
        root.render(
          <IconComponent
            color="#e75555"
            showGradient
            name="details"
            toolTipContent={fileRemovedTooltipContent}
            position="bottom"
            width="1.5rem"
            height="1.5rem"
          />,
        );
      }
    }
    return container;
  };

  const eyeIconHeaderTemplate = () => {
    const container = document.createElement("div");
    const root = createRoot(container);

    if (getIsViewerDependencyLoaded({ isModelStructureReady, isSettingsApplied, viewerImpl, hwv: CubeGlobal.hwv })) {
      let icon: string;
      const totalVisibleMeasurements = Object.values(displayMeasurements).filter(Boolean).length;
      if (measurementsListRef.current.length === totalVisibleMeasurements) {
        icon = "EyeVisible";
      } else if (totalVisibleMeasurements) {
        icon = "EyePartialOpen";
      } else {
        icon = "EyeNotVisible";
      }

      root.render(<IconComponent name={icon} onClick={headerEyeIconClickHandler} width="1.5rem" height="1.5rem" />);
    }

    return container;
  };

  const nestedMeasurementTemplate: MeasurementCellTemplate = (data, value, a, b, args) => {
    const container = document.createElement("div");
    if (!args.__isDummy) {
      container.classList.add("flex");
      const root = createRoot(container);

      // Every display unit — and its `factor` / `type` / `selectedSymbol` — is `.optional()` on the
      // estimate response, so an estimate with an unconfigured dimension really does reach here with
      // holes; a hole simply cannot match a nested measurement's type.
      const displayUnits = [
        estimateData.linearDisplayUnit,
        estimateData.areaDisplayUnit,
        estimateData.volumeDisplayUnit,
        estimateData.countDisplayUnit,
        estimateData.angleDisplayUnit,
      ];
      let nestedMeasurements = JSON.parse(args.nestedMeasurements!);

      if (source === ACTIONS.measurementSources.MEASUREMENTS && !nestedMeasurements) {
        nestedMeasurements = {
          data: getNestedMeasurements(args.measurementId!).map((measurement, index: number) => ({
            originalValue: measurement.value,
            alteredValue: measurement.value,
            type: measurement.type.value,
            name: measurement.name === "" ? `Measurement ${index + 1}` : measurement.name,
          })),
          total: {
            originalValue: args.quantity! * args.multiplier!,
            alteredValue: args.quantity! * args.multiplier!,
            type: args.type,
            name: "Measurement",
          },
        };
      }

      if (get(nestedMeasurements, "data.length")) {
        root.render(
          <ChipListComponent>
            <ChipsDirective>
              {nestedMeasurements.data.map((nestedMeasurement: NestedMeasurementChip, index: number) => {
                const { name, type, alteredValue } = nestedMeasurement;
                // `findLast` keeps the original precedence: the five sequential `if`s this replaces
                // let the last matching dimension win.
                const matchedUnit = displayUnits.findLast((unit) => unit?.type?.name === type);
                const selectedSymbol = matchedUnit?.selectedSymbol?.name ?? "";
                const factor = matchedUnit?.factor ?? 1;

                const valueInDisplayUnits = ACTIONS.getDisplayQuantity({
                  quantity: alteredValue,
                  factor,
                }).quantity;
                const result = `${name}: ${ACTIONS.getEstimateNumberValueBasedOnSettings(
                  valueInDisplayUnits,
                  estimateData,
                  "measurement",
                )} ${selectedSymbol}`;

                return <ChipDirective key={`${args.id}_${index}`} text={result} />;
              })}
            </ChipsDirective>
          </ChipListComponent>,
        );
      }
    }
    return container;
  };

  const getMeasurementFromId = (id: string) => {
    const getAllMeasurements: CubeMeasureMarkup[] = getViewer().hwv.measureManager.getAllMeasurements();
    return getAllMeasurements.find((record) => id == record.cubeUniqueId);
  };

  const handleSaveMeasurement = async (measurement: CubeMeasureMarkup | null, fetchData = true) => {
    // HOOPS' `finalizeMeasurement` triggers "measurementCreated" with whatever it is handed, so
    // this boundary is reachable with no markup at all when an operator finalizes an empty
    // measurement. Guarded here too, since the throw would surface as an unhandled rejection.
    if (!measurement) return;
    if (TEMP_MARKUP_NAMES.has(measurement._name)) return;
    if (takeOffItemId) loadMarkupStyles(measurement, takeoffDataRef.current);
    await saveMeasurements(measurement, fetchData);
  };

  const viewerCallbacks = {
    measurementDeleted: () => {
      dispatch(updateMeasurementAttributePopup(false, null, null));
    },
    measurementCreated: async (measurement: CubeMeasureMarkup | null) => {
      if (!startStopRef.current) return;

      activateOperator(OperatorId.Select);
      await handleSaveMeasurement(measurement);
    },
    sheetDeactivated: () => {
      setCurrentSheetId(-1);
    },
    sheetActivated: (sheetId: SheetId) => {
      setCurrentSheetId(sheetId);
    },
  };

  const measurementsCleanup = () => {
    if (startStopRef.current) {
      dispatch(setIsMeasurementStarted({ status: false }));
      // Reached only after `startStopMeasurement` has run, which is a viewer-only path — and the
      // viewer is the caller that supplies this callback.
      quantityTakeoffToolbarVisibility?.(false);
      hideAllTools();
    }

    if (viewerImpl) {
      viewerImpl.activateOperator(OperatorId.Select);
      viewerImpl.unsetCallbacks(viewerCallbacks);
    }

    if (source === ACTIONS.measurementSources.MEASUREMENTS) {
      removeAllMarkupsFromViewer({ unmount: true });
      dispatch(ACTIONS.setMeasurements([]));
      dispatch(ACTIONS.setMeasurementsModalStatus({ status: false }));
      dispatch(setEstimateFieldsNeedtoRefresh(estimateFieldsNeedToRefreshIntialState));
    }

    setStartStop(false);
  };

  const shouldAttachViewerCallbacks =
    (source === ACTIONS.measurementSources.MEASUREMENTS || source === ACTIONS.measurementSources.TAKEOFFS) &&
    viewerImpl;

  useViewerCallbacks({
    // `CallbackMap.measurementCreated` is declared `(measurement: MeasureMarkup) => void` in the
    // vendored hoops-web-viewer.d.ts, and `MeasureMarkup` declares `_name`, `_measurementValue` and
    // `_unitMultiplier` PROTECTED — so CUBE's own extension of the markup (`CubeMeasureMarkup`,
    // which reads exactly those three) is unrelated to it in both directions, and under
    // strictFunctionTypes no non-asserted handler signature can satisfy the slot. Single
    // registration boundary; `measurementsCleanup` hands `unsetCallbacks` this same object.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- vendored @ts3d-hoops/web-viewer CallbackMap over a class with protected members, see above
    viewerCallbacks: shouldAttachViewerCallbacks ? (viewerCallbacks as typeof viewerCallbacks & CallbackMap) : null,
  });

  const componentMounted = async () => {
    if (
      source === ACTIONS.measurementSources.MEASUREMENTS &&
      ACTIONS.isEstimateRequiredPermission(estimateData, RESOURCE_VIEW.id)
    ) {
      setShowLoader(true);
      await fetchMeasurements();
      setShowLoader(false);
      dispatch(ACTIONS.setMeasurementsModalStatus({ status: true }));
    }
  };

  useEffect(() => {
    takeoffsModalStatusRef.current = HoopsModalStatus.Takeoffs;
  }, [HoopsModalStatus.Takeoffs]);

  useEffect(() => {
    takeoffDataRef.current = takeoffData;
  }, [takeoffData]);

  useEffect(() => {
    startStopRef.current = startStop;
  }, [startStop]);

  useEffect(() => {
    measurementsModalStatusRef.current = measurementsModalStatus;
  }, [measurementsModalStatus]);

  useEffect(() => {
    activeDerivedMeasurementTabRef.current = activeDerivedMeasurementTab;
  }, [activeDerivedMeasurementTab]);

  const initMeasurementSelection = async () => {
    if (
      urlSearchParams.get("measurementId") &&
      source === ACTIONS.measurementSources.MEASUREMENTS &&
      ACTIONS.isEstimateRequiredPermission(estimateData, RESOURCE_VIEW.id)
    ) {
      const getParams = {
        value: {
          params: {
            estimateId,
            measurementId: urlSearchParams.get("measurementId") ?? undefined,
            fileVersionId,
            versionId: estimateVersionId,
          },
        },
      };
      const needToSelectMeasurmentData = await ACTIONS.getMeasurementsList(getParams);

      if (get(needToSelectMeasurmentData, "data.result.length") && needToSelectMeasurmentData.data.result) {
        // The SCHEMA GAP note that used to sit here is gone: `getMeasurementsResponse.result` is now
        // `z.array(measurementItemSchema)`, so the row's members are visible. The optional chain is
        // the index read itself (`noUncheckedIndexedAccess`), which the length check above covers.
        const sheetId = needToSelectMeasurmentData.data.result[0]?.sheetId || -1;
        if (sheetId !== currentSheetIdRef.current) {
          if (sheetId === -1) {
            await getViewer().hwv.sheetManager.deactivateSheets();
          } else {
            await getViewer().hwv.sheetManager.setActiveSheetId(sheetId);
          }
        }
      } else {
        setCurrentSheetId(-1);
      }
    }
  };

  useEffect(() => {
    return measurementsCleanup;
  }, []);

  useEffect(() => {
    if (
      source === ACTIONS.measurementSources.MEASUREMENTS &&
      getIsViewerDependencyLoaded({ isModelStructureReady, isSettingsApplied, viewerImpl, hwv: CubeGlobal.hwv })
    ) {
      initMeasurementSelection();
    }
  }, [isSettingsApplied, isModelStructureReady]);

  useEffect(() => {
    currentSheetIdRef.current = currentSheetId;
    if (source == ACTIONS.measurementSources.MEASUREMENTS && currentSheetId !== null) {
      removeAllMarkupsFromViewer({ unmount: false });
      setItemsMarked([]);
      setEnableToolbarButtons(false);
      setShowTakeoffsList(false);
      isInitialMarkupLoadDone.current = false;
      componentMounted();
    }
  }, [currentSheetId]);

  useEffect(() => {
    if (
      source === ACTIONS.measurementSources.MEASUREMENTS &&
      getIsViewerDependencyLoaded({ isModelStructureReady, isSettingsApplied, viewerImpl, hwv: CubeGlobal.hwv }) &&
      ACTIONS.isEstimateRequiredPermission(estimateData, RESOURCE_VIEW.id)
    ) {
      measurementsListRef.current = [...measurementsList];
      if (measurementsList.length && !isInitialMarkupLoadDone.current) {
        removeAllMarkupsFromViewer({ unmount: false });
        loadAllMarkups();
        isInitialMarkupLoadDone.current = true;
      } else {
        const obj: Record<string, boolean> = {};
        setDisplayMeasurements((prev) => {
          for (const measurement of measurementsList) {
            const { measurementId } = measurement;
            if (measurementId != null) {
              if (Object.hasOwn(prev, measurementId)) {
                obj[measurementId] = prev[measurementId]!;
              } else {
                obj[measurementId] = true;
              }
            }
          }
          return obj;
        });
      }
    }
  }, [measurementsList, isModelStructureReady, isSettingsApplied]);

  useEffect(() => {
    if (source === ACTIONS.measurementSources.MEASUREMENTS && dataGrid.current) {
      dataGrid.current.dataset = measurementsList;
    }
  }, [measurementsList, dataGrid, source]);

  useEffect(() => {
    if (source === ACTIONS.measurementSources.MEASUREMENTS && estimateFieldsNeedToRefresh.measurements) {
      const setData = ACTIONS.prepareEstimationFieldNeedToRefresh({
        HoopsModalStatus,
        measurements: false,
        estimateFieldsNeedToRefresh,
      });
      dispatch(setEstimateFieldsNeedtoRefresh(setData));
    }
  }, [measurementsList, HoopsModalStatus, estimateFieldsNeedToRefresh, source]);

  useEffect(() => {
    if (source === ACTIONS.measurementSources.MEASUREMENTS && estimateFieldsNeedToRefresh.measurements) {
      fetchMeasurements();
    }
  }, [estimateFieldsNeedToRefresh.measurements, fetchMeasurements, source]);

  useEffect(() => {
    itemsMarkedRef.current = itemsMarked;
    if (itemsMarked.length !== 1) {
      setDerivedMeasurementOptions([]);
      setShowDeriveMeasurementsModal(false);
      return;
    }

    const derivedMeasurementTypes: DerivedMeasurementOption[] = [];
    const measurement = getMeasurementFromId(itemsMarked[0]!);
    // 3D case
    if (measurement) {
      if (measurement.derivePerimeterMeasurement) derivedMeasurementTypes.push(typePerimeter);
      if (measurement.deriveSurfaceAreaMeasurement) derivedMeasurementTypes.push(typeSurfaceArea);
      if (measurement.deriveCircumferenceMeasurement) derivedMeasurementTypes.push(typeCircumference);
      if (measurement.deriveLengthSegmentsMeasurement) derivedMeasurementTypes.push(typeLengthSegments);
    }

    setDerivedMeasurementOptions(derivedMeasurementTypes);
  }, [itemsMarked]);

  useEffect(() => {
    if (source == ACTIONS.measurementSources.MEASUREMENTS) {
      displayMeasurementsRef.current = displayMeasurements;
    }
  }, [displayMeasurements, source]);

  const onTabSelect = (value: string) => {
    flagToCheckRowSelectionRef.current = true;
    setActiveDerivedMeasurementTab(value);
  };

  const getDeriveMeasurementGrid = useCallback(() => {
    return (
      <DeriveMeasurements
        key={activeDerivedMeasurementTab}
        dataSource={derivedMeasurementsRef.current.data[activeDerivedMeasurementTab!]!}
        derivedMeasurementsSelectingEvents={derivedMeasurementsSelectingEvents}
        selectedMeasurements={derivedMeasurementsRef.current.selectedRecords[activeDerivedMeasurementTab!]!}
      />
    );
  }, [activeDerivedMeasurementTab]);

  const getDeriveMeasurementsModal = () => (
    <div className="fixed inset-0 z-[2] w-screen h-screen bg-[var(--background-scrim)]">
      <div className="fixed top-[20%] start-[25%] z-[3] w-[43.75rem] rounded-md bg-white border border-[#ecf3fe] shadow-[0.25rem_0.5rem_0.5rem_0_rgb(0_0_0_/_15%)]">
        <div className="flex items-center justify-between flex-row bg-[#f6f9fc] pt-[1.125rem] pb-[1.125rem] pe-6 ps-10">
          <Text>{t("Derive Measurements")}</Text>
          <GrClose size="20" onClick={finishDeriveMeasurements} className="cursor-pointer" />
        </div>
        <div className="px-10 border-0 border-b border-button_light">
          <Box sx={{ paddingBottom: "0.1875rem" }}>
            <Tabs
              value={activeDerivedMeasurementTab}
              onChange={(event, selectedTab: string) => onTabSelect(selectedTab)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                "& .MuiTabs-indicator": {
                  backgroundColor: "#6BBBE3",
                  height: "0.25rem",
                  borderRadius: "1.25rem",
                },
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontSize: "1rem",
                  color: "#192048",
                  minWidth: "auto",
                  marginInlineEnd: "3rem",
                  padding: 0,
                },
                "& .Mui-selected": {
                  color: "#192048",
                  fontWeight: 600,
                },
              }}
            >
              {derivedMeasurementOptions.map(({ text, value }) => (
                <Tab label={text} value={value} key={value} />
              ))}
            </Tabs>
          </Box>
        </div>
        <div className="pt-2.5 pb-5 pe-6 ps-10">
          <div className="h-[12.5rem] overflow-auto">{getDeriveMeasurementGrid()}</div>
          <ButtonComponent
            buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
            onClick={(e: React.MouseEvent) => saveDerivedMeasurements(e)}
            disabled={!derivedMeasurementSelected}
          >
            <Text>{t("Create")}</Text>
          </ButtonComponent>
        </div>
      </div>
    </div>
  );

  const getTakeoffsListContainer = () => (
    <div className="flex flex-col absolute z-[10] bg-white shadow-[0_0.25rem_0.25rem_0_rgb(0_0_0_/_25%)] p-5 overflow-auto">
      {takeoffsList.length ? (
        <>
          <div className="h-[9.375rem] w-[12.5rem] overflow-auto">
            {takeoffsList.map((takeoff) => (
              <div key={takeoff.takeOffItemId} className="flex flex-row">
                <input
                  type="checkbox"
                  id={takeoff.takeOffItemId}
                  name={takeoff.takeOffItemId}
                  onClick={checkboxClicked}
                />
                <label className="ms-2.5" htmlFor={takeoff.takeOffItemId}>
                  {takeoff.name}
                </label>
              </div>
            ))}
          </div>
          <ButtonComponent
            buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
            onClick={attachMeasurementsToTakeoffs}
            disabled={!takeoffsChecked.length}
          >
            <Text>{t("Add")}</Text>
          </ButtonComponent>
        </>
      ) : (
        <small>
          <strong>
            <Text>{t("No more takeoffs available")}</Text>
          </strong>
        </small>
      )}
      <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={startNewTakeoff} className="mt-2.5">
        <Text>{t("Create New Takeoff")}</Text>
      </ButtonComponent>
    </div>
  );

  // Takes the id as the callers hold it: both the grid row's `id` and its `measurementId` are
  // `.optional()` on `measurementItemSchema`, and a missing one simply matches no markup below.
  const getNestedMeasurements = (cubeUniqueId: string | undefined) => {
    if (viewerImpl?.hwv) {
      const allMeasurements: CubeMeasureMarkup[] = viewerImpl.hwv.measureManager.getAllMeasurements();
      const selectedMeasurement = allMeasurements.find((record) => record.cubeUniqueId === cubeUniqueId);

      if (get(selectedMeasurement, "getNestedMeasurements")) {
        // KNOWN BUG, preserved verbatim: the `_.get` above proves the member exists but does not
        // narrow `selectedMeasurement` itself, which `find` may still have missed. Erased assertions.
        const nestedMeasurements = selectedMeasurement!.getNestedMeasurements!();
        return nestedMeasurements;
      }
    }

    return [];
  };

  const measurementTypeTemplate: MeasurementCellTemplate = (data, value, a, b, params) => {
    if (params.__isDummy) {
      return "";
    } else {
      if (params.markup == "{}" && params.elements) {
        return "3D";
      }
      // Direct read for the same reason as `unitsTemplate`: the row type's passthrough index
      // signature makes a dotted `_.get` path resolve to `unknown`/`{}`. A SlickGrid formatter must
      // return a string, and the unit-type name is present on every real row — `unit`, `unit.type`
      // and `unit.type.name` are all required fields of `measurementDocument`
      // (measurements.collection.ts) — so the empty string is only the dummy/legacy fallback.
      return params.unit?.type?.name ?? "";
    }
  };

  const nameEditTemplate = () => (
    <TextFieldComponent
      onChange={(e) => setEditingRowValue((prev) => ({ ...prev, name: e.target.value }))}
      defaultValue={editingRowValue["name"]}
    />
  );

  const multiplierEditTemplate = () => (
    <TextFieldComponent
      onChange={(e) => setEditingRowValue((prev) => ({ ...prev, multiplier: e.target.value }))}
      type="number"
      defaultValue={editingRowValue["multiplier"]}
    />
  );

  const gridColumns = useMemo(
    () =>
      getMeasurementsListColumns({
        source,
        nameTemplate,
        hightlightMeasurementTemplate,
        tagsTemplate,
        eyeIconHeaderTemplate,
        eyeIconTemplate,
        isFilterAllowed,
        measurementTemplate,
        measurementTypeTemplate,
        takeOffsTemplate,
        nestedMeasurementTemplate,
        measuredValueTemplate,
        unitsTemplate,
        nameEditTemplate,
        multiplierEditTemplate,
      }),

    [editingRowValue.measurementId, displayMeasurements, isSettingsApplied, isModelStructureReady],
  );

  const getRowHeight = () => (source === ACTIONS.measurementSources.ESTIMATE ? 60 : 40);

  // `gridColumns` is `getMeasurementsListColumns()`'s return, which is an array on every branch, so
  // the `gridColumns &&` guard this used to carry could never be false.
  const getDataGrid = () => (
    <div className="flex overflow-auto h-full w-full">
      <SlickGrid1<MeasurementGridRow>
        ref={dataGrid}
        enableWrapText={true}
        enableSelectAllCheckbox={true}
        showLoader={showLoader}
        showPageCount={
          ![ACTIONS.measurementSources.TAKEOFF_SIDEBAR, ACTIONS.measurementSources.TAKEOFFS].includes(source)
        }
        // BLOCKED on `src/features/estimation/components/Estimation/EstimateHelper.ts`: `Column` is
        // declared an `interface` there, and interfaces get no implicit index signature — so
        // `getMeasurementsListColumns()`'s return does not satisfy the `Record<string, unknown>` arm
        // of `withDynamicColumns()`, the shared brander for column lists built elsewhere. Make
        // `Column` a type alias (as `ChargesGridColumn` in Charges.tsx documents) and this becomes
        // `columnDefinitions={withDynamicColumns<MeasurementGridRow>(gridColumns)}`.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- see above: withDynamicColumns() cannot accept an interface-typed column list
        columnDefinitions={gridColumns as typeof gridColumns & ValidatedColumnDefinition<MeasurementGridRow>[]}
        rowHeight={getRowHeight()}
        disableCheckBoxSelection={
          ![
            ACTIONS.measurementSources.MEASUREMENTS,
            ACTIONS.measurementSources.TAKEOFFS,
            ACTIONS.measurementSources.REFERENCE_MEASUREMENT,
          ].includes(source) || isVersionMode
        }
        enableSortingOption
        enableFilteringOption
        onSelectedRowIdsChanged={selectingEvents}
        dataset={source == ACTIONS.measurementSources.MEASUREMENTS ? measurementsList : undefined}
        remoteDataSource={source !== ACTIONS.measurementSources.MEASUREMENTS ? estimateMeasurementsUrl : undefined}
        infiniteScroll
        // `query` is an optional prop, and the MEASUREMENTS source has no remote query at all — it
        // feeds the grid through `dataset` — so "no query" is the absence of one, not a null.
        query={gridQuery ?? undefined}
        setGridReady={dataBound}
        onClick={recordClick}
        isEditMode={!!editingRowValue.measurementId}
        toolbar={getMeasurementToolbarItems()}
        toolbarClickHandler={measurementToolbarClickHandler}
      />
    </div>
  );

  const handleModifyNestedMeasurement = () => {
    const selectedRecord: MeasurementGridRow = dataGrid
      .current!.slickGrid.getSelectedRows()
      .map((data) => dataGrid.current!.slickGrid.getDataItem(data))[0];
    let nestedMeasurements;

    if (selectedRecord.nestedMeasurements == null) {
      nestedMeasurements = {
        data: getNestedMeasurements(selectedRecord.id).map((measurement, index: number) => ({
          originalValue: measurement.value,
          alteredValue: measurement.value,
          type: measurement.type.value,
          name: measurement.name === "" ? `Measurement ${index + 1}` : measurement.name,
        })),
        total: {
          originalValue: selectedRecord.quantity! * selectedRecord.multiplier!,
          alteredValue: selectedRecord.quantity! * selectedRecord.multiplier!,
          type: selectedRecord.type,
          name: "Measurement",
        },
      };
    } else {
      nestedMeasurements = JSON.parse(selectedRecord.nestedMeasurements);
    }

    dispatch(
      // `SelectedMeasurementRecord` declares exactly the members the nested-measurements modal reads
      // back, so those are what is parked in redux. The whole grid row used to be spread in — which
      // carried the passthrough row's `unknown`-typed extras and is what forced the assertion; the
      // modal (NestedMeasurements.tsx) only ever reads `nestedMeasurements`, `measurementId` and
      // `estimateId` off it.
      setSelectedNestedMeasurements({
        measurementId: selectedRecord.measurementId,
        name: selectedRecord.name,
        type: selectedRecord.type,
        quantity: selectedRecord.quantity,
        scale: selectedRecord.scale,
        markup: selectedRecord.markup,
        takeOffItemId: selectedRecord.takeOffItemId ?? undefined,
        fileVersionId: selectedRecord.fileVersionId,
        estimateId: selectedRecord.estimateId,
        referenceMeasurements: selectedRecord.referenceMeasurements,
        formula: selectedRecord.formula,
        manualUnit: selectedRecord.manualUnit,
        isManualMeasurement: selectedRecord.isManualMeasurement,
        sheetId: selectedRecord.sheetId,
        nestedMeasurements,
      }),
    );

    // Read directly, not through `_.get`: `HoopsModalState` is a `Record<string, ModalEntry>`, so a
    // dotted lodash path resolves to `undefined` — the check could never be anything but true. The
    // optional chain is the index read itself (`noUncheckedIndexedAccess`).
    if (!HoopsModalStatus.NestedMeasurements?.show) {
      dispatch(
        setModalStatus({
          ...HoopsModalStatus,
          NestedMeasurements: {
            show: true,
          },
        }),
      );
    }
  };

  const handleAddCustomMeasurement = (data?: ACTIONS.ManualMeasurementState) => {
    setManualMeasurementModal((prev) => ({ ...prev, show: true, data }));
  };

  const handleCloseManualMeasurement = () => {
    setManualMeasurementModal({ show: false, data: null });
  };

  const handleCreateManualMeasurement = async (
    manualData: ACTIONS.ManualMeasurementState,
    measurementData?: ACTIONS.ManualMeasurementState,
  ) => {
    // The form models `unit` as a plain string (the id of the measurement unit picked in its
    // dropdown); the create/update requests want the 24-hex ObjectId, so it is validated here and
    // folded into the same "incomplete form" check an empty one already failed — a malformed id
    // could only come back rejected by the server's own `objectIdString`.
    const manualUnit = objectIdString.safeParse(manualData.unit).data;
    if (
      !manualData.name ||
      !manualData.type ||
      !Number.isFinite(manualData.value) ||
      !manualUnit ||
      (manualData.isReferenced && manualData.references.length < 1)
    ) {
      dispatch(
        showToastNotification({
          content: t("Please fill all details."),
          type: TOAST_TYPE_ERROR,
        }),
      );
    } else {
      // All four display units are `.optional()` on the estimate response, so an estimate with an
      // unconfigured dimension reaches here with holes — and a hole cannot mismatch the chosen type.
      const displayUnits = [
        estimateData.linearDisplayUnit,
        estimateData.areaDisplayUnit,
        estimateData.volumeDisplayUnit,
        estimateData.countDisplayUnit,
      ];

      for (const displayUnit of displayUnits) {
        if (displayUnit?.type?.name === manualData.type && displayUnit.id !== manualUnit) {
          dispatch(
            showToastNotification({
              content: t("Value and units changed to match estimate's display units."),
              type: TOAST_TYPE_NORMAL,
            }),
          );
        }
      }

      if (get(measurementData, "measurementId")) {
        await ACTIONS.updateMeasurement({
          measurementId: measurementData!.measurementId,
          name: manualData.name,
          type: manualData.type,
          quantity: manualData.value!,
          manualUnit,
          // BLOCKED on `src/actions/estimation.ts`: `ReferenceMeasurement.variable` is declared
          // optional there, while `referenceMeasurementSchema` requires it — and the only producer,
          // `generateAddData` in CentralManualCostComponent.tsx, always assigns one. Make it
          // required there and both assertions below go away.
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- see above: ReferenceMeasurement.variable is optional in the FE type only
          referenceMeasurements: manualData.references as NonNullable<MeasurementItem["referenceMeasurements"]>,
          formula: manualData.formula,
          estimateId,
        });
      } else {
        // SCHEMA GAP: `createMeasurementRequest` types `type` as the five-value unit-type enum,
        // while the manual form supplies a free-text type.
        await ACTIONS.createMeasurement({
          measurementId: createUuid(),
          name: manualData.name,
          type: manualData.type as NonNullable<MeasurementItem["type"]>,
          quantity: manualData.value!,
          manualUnit,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- see the note on the update branch above: ReferenceMeasurement.variable is optional in the FE type only
          referenceMeasurements: manualData.references as NonNullable<MeasurementItem["referenceMeasurements"]>,
          formula: manualData.formula,
          scale: "1:1",
          markup: "{}",
          fileVersionId,
          estimateId,
          isManualMeasurement: true,
          // As in `saveMeasurements`: no active sheet is the ABSENCE of a sheet id, and the null this
          // ref holds fails `sheetId: z.number().optional()` outright.
          sheetId: currentSheetIdRef.current ?? undefined,
        });
      }

      handleCloseManualMeasurement();
      reloadMeasurements(true);
    }
  };

  const getErrorMessageforSelectedMeasurement = () => {
    if (
      measurementsList
        .filter((measurement) => measurement.nature !== "Static")
        .map((val) => val.measurementId)
        .some((item) => itemsMarked.includes(item!))
    ) {
      return t("Only Static Measurements can be added to Takeoffs");
    }
    if (
      new Set(
        measurementsList.filter((item) => itemsMarked.includes(item.measurementId!)).map((val) => val.unit!.type!.id),
      ).size !== 1
    ) {
      return t("Only Measurements with similar Type can be added to Takeoffs");
    }
    return "";
  };

  const checkSystemGeneratedMeasurementSelected = () => {
    return (measurementsList as MeasurementGridRow[]).some(
      (measurement) => measurement.isSystemGenerated && itemsMarked.includes(measurement.measurementId!),
    );
  };

  const editClickHandler = () => {
    // The toolbar's Edit button is only enabled with exactly one marked row, so this hits — but the
    // lookup is done ONCE and checked, instead of being repeated and asserted: the second lookup
    // could equally have missed, and dereferencing the miss threw.
    const selectedRecord = measurementsList.find((item) => itemsMarked.includes(item.measurementId!));
    if (!selectedRecord) return;
    if (selectedRecord.isManualMeasurement) {
      handleManualMeasurementEdit(selectedRecord);
    } else {
      setEditingRowValue({
        measurementId: selectedRecord.measurementId,
        name: selectedRecord.name,
        multiplier: selectedRecord.multiplier,
        takeOffs: selectedRecord.takeOffs,
      });
    }
  };

  const cancelEditMeasurement = () => {
    setEditingRowValue({});
  };

  const saveClickHandler = async () => {
    setButtonLoading(true);
    // The multiplier editor is a text field, but `editClickHandler` seeds the row with the stored
    // number — `String()` covers both forms, and a blank/absent value still falls back to 1 exactly
    // as `parseFloat`'s NaN did.
    const multiplier = Number.parseFloat(String(editingRowValue.multiplier));
    await ACTIONS.updateMeasurement({
      measurementId: editingRowValue.measurementId,
      name: editingRowValue.name,
      multiplier: Number.isNaN(multiplier) ? 1 : multiplier,
      estimateId,
    });
    reloadMeasurements(true);

    if (editingRowValue.takeOffs?.length) {
      const setData = ACTIONS.prepareEstimationFieldNeedToRefresh({
        HoopsModalStatus,
        takeoffs: editingRowValue.takeOffs!.map((takeoff) => takeoff.takeOffItemId!),
        estimateFieldsNeedToRefresh,
      });
      dispatch(setEstimateFieldsNeedtoRefresh(setData));
    }
    setButtonLoading(false);
    cancelEditMeasurement();
  };

  const stopMeasurementHandler = () => {
    if (startStop) {
      startStopMeasurement();
    } else {
      handleCloseManualMeasurement();
    }
  };

  const handleDropDownSelectionHandler = (args: SelectEventArgs) => {
    const buttonClicked = args.item.id;
    if (buttonClicked === "standard") {
      startStopMeasurement();
    } else if (buttonClicked == "manual") {
      handleAddCustomMeasurement();
    }
  };

  const dropDownButtonItems = () => [
    {
      id: "standard",
      text: "Start Measuring",
      disabled: !getIsViewerDependencyLoaded({
        isModelStructureReady,
        isSettingsApplied,
        viewerImpl,
        hwv: CubeGlobal.hwv,
      }),
    },
    { id: "manual", text: "Create Manual Measurement", enabled: false },
  ];

  const getDeleteButtonText = () => (source === ACTIONS.measurementSources.TAKEOFFS ? t("Remove") : t("Delete"));

  const isSystemGenerated = checkSystemGeneratedMeasurementSelected();
  const commonDisabled = isVersionMode || !actionPermissionAllowed || isSystemGenerated;

  const startMeasurementTemplate = () => {
    if (startStop || manualMeasurementModal.show) {
      return (
        <ButtonComponent
          disabled={
            isVersionMode ||
            !getIsViewerDependencyLoaded({
              isModelStructureReady,
              isSettingsApplied,
              viewerImpl,
              hwv: CubeGlobal.hwv,
            }) ||
            !actionPermissionAllowed
          }
          onClick={stopMeasurementHandler}
          buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
        >
          <div className="flex gap-2 items-center">
            <IconComponent name="Pause" width="1rem" height="1rem" />
            <Text>{t("Stop")}</Text>
          </div>
        </ButtonComponent>
      );
    }
    return (
      <SelectDropDown
        select={handleDropDownSelectionHandler}
        items={dropDownButtonItems()}
        disabled={
          isVersionMode ||
          !getIsViewerDependencyLoaded({ isModelStructureReady, isSettingsApplied, viewerImpl, hwv: CubeGlobal.hwv }) ||
          !actionPermissionAllowed
        }
        allowButtonPadding={false}
        cssClass="border-none"
        buttonType="dropdownBackgroundButton"
      >
        <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}>
          <span className="inline-block">
            <div className="flex gap-2 items-center">
              <IconComponent name="Start" width="0.75rem" height="0.75rem" />
              <Text>{t("Start")}</Text>
            </div>
          </span>
        </ButtonComponent>
      </SelectDropDown>
    );
  };

  const addToTakeoffsTemplate = () => {
    const errorMessage = getErrorMessageforSelectedMeasurement();
    const button = (
      <ButtonComponent
        buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
        disabled={!actionPermissionAllowed || isVersionMode || !!errorMessage || isSystemGenerated}
        onClick={addToTakeoffsClickHandler}
      >
        <Text>{t("Add to takeoffs")}</Text>
      </ButtonComponent>
    );
    return (
      <div>
        {errorMessage ? (
          <ToolTipComponent position="bottom" content={errorMessage}>
            {button}
          </ToolTipComponent>
        ) : (
          button
        )}
        {showTakeoffsList && getTakeoffsListContainer()}
      </div>
    );
  };

  const isMeasurementSource = source === ACTIONS.measurementSources.MEASUREMENTS;
  const isTakeoffSource = source === ACTIONS.measurementSources.TAKEOFFS;

  const getMeasurementToolbarItems = () => {
    if (openAsPopup) return [];
    if (editingRowValue.measurementId) {
      return [
        {
          id: MEASUREMENT_TOOLBAR_IDS.SAVE,
          name: t("Save"),
          prefixIcon: "Update",
          disabled: buttonLoading,
        },
        {
          id: MEASUREMENT_TOOLBAR_IDS.CANCEL,
          name: t("Cancel"),
          prefixIcon: "close",
          disabled: buttonLoading,
        },
      ];
    }

    // The toolbar array holds both plain buttons and custom-template entries; `ToolbarItem` declares
    // `text`, while these entries use `name`, so it is extended locally.
    const items: MeasurementToolbarItem[] = [];

    if (isMeasurementSource) {
      items.push({
        id: MEASUREMENT_TOOLBAR_IDS.START,
        template: startMeasurementTemplate,
      });
    }

    if (isMeasurementSource && enableToolbarButtons && itemsMarked.length === 1) {
      items.push({
        id: MEASUREMENT_TOOLBAR_IDS.EDIT,
        name: t("Edit"),
        prefixIcon: "edit",
        disabled: commonDisabled,
      });
    }

    if ((isMeasurementSource || (isTakeoffSource && takeOffIncludedMeasuremnt)) && enableToolbarButtons) {
      items.push({
        id: MEASUREMENT_TOOLBAR_IDS.DELETE,
        name: getDeleteButtonText(),
        disabled: commonDisabled,
      });
    }

    if (isMeasurementSource && enableToolbarButtons && itemsMarked.length > 0) {
      items.push({
        id: MEASUREMENT_TOOLBAR_IDS.ADD_TO_TAKEOFFS,
        template: addToTakeoffsTemplate,
      });
    }

    if (isMeasurementSource && enableToolbarButtons && itemsMarked.length === 1) {
      items.push({
        id: MEASUREMENT_TOOLBAR_IDS.DUPLICATE,
        name: t("Duplicate"),
        disabled:
          isVersionMode ||
          !actionPermissionAllowed ||
          isSystemGenerated ||
          measurementsList.find((measurement) => measurement.measurementId == itemsMarked[0])?.nature !== "Static",
      });

      items.push({
        id: MEASUREMENT_TOOLBAR_IDS.DERIVE,
        name: t("Derive"),
        disabled:
          !derivedMeasurementOptions.length ||
          !actionPermissionAllowed ||
          startStop ||
          isSystemGenerated ||
          isVersionMode,
      });

      items.push({
        id: MEASUREMENT_TOOLBAR_IDS.SITE_MEASUREMENT,
        name: t("Site Measurement"),
        disabled: commonDisabled,
      });
    }

    return items;
  };

  const measurementToolbarClickHandler = (toolClicked: string) => {
    switch (toolClicked) {
      case MEASUREMENT_TOOLBAR_IDS.EDIT:
        editClickHandler();
        break;
      case MEASUREMENT_TOOLBAR_IDS.DELETE:
        destroyMeasurement();
        break;
      case MEASUREMENT_TOOLBAR_IDS.DUPLICATE:
        duplicateMeasurementClickHandler();
        break;
      case MEASUREMENT_TOOLBAR_IDS.DERIVE:
        beginDeriveMeasurements();
        break;
      case MEASUREMENT_TOOLBAR_IDS.SITE_MEASUREMENT:
        handleModifyNestedMeasurement();
        break;
      case MEASUREMENT_TOOLBAR_IDS.SAVE:
        saveClickHandler();
        break;
      case MEASUREMENT_TOOLBAR_IDS.CANCEL:
        cancelEditMeasurement();
        break;
      default:
        break;
    }
  };

  return estimateError ? (
    <EstimateNotFoundPage projectId={urlSearchParams.get("projectId")!} />
  ) : (
    <div
      className={
        source === ACTIONS.measurementSources.MEASUREMENTS
          ? "h-full flex flex-col px-5"
          : "flex-grow h-full overflow-auto flex flex-col"
      }
    >
      {(source === ACTIONS.measurementSources.MEASUREMENTS || source === ACTIONS.measurementSources.TAKEOFFS) &&
        showCalibrationModal &&
        // The calibration dialog drives the viewer directly, so it only renders once the Viewer's
        // Provider has supplied both (it is the Viewer that opens it in the first place).
        viewerImpl &&
        handleCalibrationModal && (
          <CalibrationModal viewerImpl={viewerImpl} handleCalibrationModal={handleCalibrationModal} />
        )}

      {source === ACTIONS.measurementSources.MEASUREMENTS && showNewTakeoffModal && (
        <CreateNewTakeoffModal estimateId={estimateId} closeTakeoffModal={closeTakeoffModal} />
      )}

      {showDeriveMeasurementsModal && getDeriveMeasurementsModal()}

      <div className="h-full overflow-auto" id="measurements_grid_unique">
        {getDataGrid()}
      </div>

      {duplicateModalAndData.show && (
        <DuplicateTakeoffModal
          closeDuplicateTakeoffModal={closeDuplicateMeasurementModal}
          copyTakeoff={copyMeasurement}
          isDuplicateMeasurement
        />
      )}

      {manualMeasurementModal.show && (
        <ManualMeasurement
          measurementData={manualMeasurementModal.data ?? undefined}
          handleCloseManualMeasurement={handleCloseManualMeasurement}
          estimateId={estimateId}
          handleCreateManualMeasurement={handleCreateManualMeasurement}
          fileVersionId={fileVersionId}
        />
      )}
    </div>
  );
});

Measurements.displayName = "Measurements";
export default Measurements;
